import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { Box, Paper, styled, Typography } from "@mui/material";
import { Chart as ChartJS, registerables } from "chart.js";
import { COLORS } from "colors/color";
import internshipIcon from "../../assets/img/internshipIcon.png";
import { useTranslation } from "react-i18next";

ChartJS.register(...registerables);

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "none",
  transition: "box-shadow 0.3s ease-in-out",
  border: '1px solid #D9D9D9',
  height: '100%',
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
  },
}));

const CardHeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const CardBodyWrapper = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  height: "70%",
  width: "100%",
  position: "relative",
}));

const InternshipStatistics = ({ userId, apiUrl }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${apiUrl}authentication/scraped-posts/${userId}/`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();
        const formattedData = Object.entries(data).map(
          ([internshipName, internCount]) => ({
            name: internshipName,
            value: parseInt(internCount),
          })
        );
        setChartData(formattedData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, apiUrl]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#000",
        titleColor: "#fff",
        bodyColor: "#fff",
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          title: (context) => {
            const dataIndex = context[0].dataIndex;
            return chartData[dataIndex].name;
          },
          label: (context) => `${t("charts.scraping_statistics.tooltip.candidates", {
            count: context.raw,
          })}`,
        },
        responsive: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        responsive: true,
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: "#E2E8F0",
          lineWidth: 0.5,
        },
        ticks: {
          padding: 8,
          color: "#64748B",
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          stepSize: 1,
          precision: 0,
          callback: function(value) {
            return value >= 1000 ? (value/1000).toFixed(1) + 'k' : value;
          }
        },
        beginAtZero: true,
        responsive: true,
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
        borderSkipped: false,
      },
    },
    barPercentage: 0.6,
    categoryPercentage: 0.8,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  const colors = [
    "#E2E8F0", 
    "#94A3B8", 
    "#475569", 
    "#1E293B", 
    "#003CBA"
  ];

  const truncateText = (text, maxLength = 6) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const data = {
    labels: chartData.map((item) => truncateText(item.name)),
    datasets: [
      {
        label: t("charts.scraping_statistics.title"),
        data: chartData.map((item) => item.value),
        backgroundColor: colors.slice(0, chartData.length),
        borderWidth: 0,
      },
    ],
  };

  if (loading) return <div>{t("general.loading")}</div>;
  if (error) return <div>{t("general.error", { message: error })}</div>;

  return (
    <StyledCard>
      <CardHeaderWrapper>
        <Box display="flex" alignItems="center">
          <Typography
           sx={{
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '16px' },
            color: '#00000080'
          }}
          >
            {/* {t("charts.scraping_statistics.title")} */} Statistiques de scraping par offre
          </Typography>
        </Box>
      </CardHeaderWrapper>
      <CardBodyWrapper>
        <Box sx={{ 
          height: { xs: 250, sm: 300, md: 350 },
          width: '100%',
          position: 'relative',
          '& canvas': {
            maxWidth: '100% !important',
            height: '90% !important'
          }
        }}>
          <Bar data={data} options={chartOptions} />
        </Box>
      </CardBodyWrapper>
    </StyledCard>
  );
};

export default InternshipStatistics;